import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Box, Group, Text } from '@mantine/core'
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ListSystemDto, ListSystemsQuery } from '@/features/systems/domain/systems.types'
import { useSystemList } from '@systems/api/systemQueries.tsx'
import { DataTable, DataTableColumn, PageHeader, StatusBadge } from '@/shared/components/ui'
import { formatDistanceToNow } from 'date-fns'
import { formatDateTime } from '@/shared/utils/dateFormatters'
import { ProtectedComponent } from '@/features/auth/components/ProtectedComponent'
import { FallbackType, UserRole } from '@/features/auth/domain/auth.types'
import { IconRefresh } from '@tabler/icons-react'
import { SystemConnectionsFilter } from '@/features/systems/components/SystemConnectionsFilter'

export const Route = createFileRoute('/_authed/systems/')({
  component: SystemManagementSection,
})

function SystemManagementSection() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState<ListSystemsQuery>({
    page: 1,
    pageSize: 20
  })

  const {data, isLoading} = useSystemList(query)

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({queryKey: ['systems']})
  }, [queryClient])

  const columns: DataTableColumn[] = [
    {
      key: 'id',
      header: 'System ID',
      render: (value: string) => (
        <Text
          ff="monospace"
          size="sm"
          c="primary"
          component={Link}
          to="/systems/$systemId"
          params={{systemId: value} as any}
          style={{textDecoration: 'none'}}
        >
          {value}
        </Text>
      ),
    },
    {
      key: 'machineName',
      header: 'Machine Name',
      render: (value: string | null) => (
        <Text size="sm">
          {value || 'N/A'}
        </Text>
      ),
    },
    {
      key: 'isOnline',
      header: 'Status',
      render: (value: boolean) => (
        <StatusBadge status={value ? 'online' : 'offline'}/>
      ),
    },
    {
      key: 'registeredAt',
      header: 'Registered',
      render: (value: string) => formatDateTime(value),
    },
    {
      key: 'isOnlineChangedAt',
      header: 'Status Changed',
      render: (value: string) => formatDistanceToNow(new Date(value)) + ' ago',
    }
  ]

  const handlePageChange = (page: number) => {
    setQuery(prev => ({...prev, page}))
  }


  function handleRowClick(system: ListSystemDto) {
    navigate({
      to: '/systems/$systemId',
      params: {systemId: system.id}
    })
  }

  return (
    <ProtectedComponent
      requiredRole={UserRole.Viewer}
      fallbackType={FallbackType.Section}
      fallbackTitle="Systems"
      fallbackAction="view systems"
    >
      <Box>
        <PageHeader
          title="Systems"
          subtitle="Manage and monitor all systems"
          backButton={false}
          actions={[
            {
              label: 'Refresh',
              icon: IconRefresh,
              onClick: handleRefresh,
              loading: isLoading,
              ariaLabel: 'refresh-systems',
              color: 'green'
            },
          ]}
        >
          <Group justify="space-between" mt="md">
            <SystemConnectionsFilter
              query={query}
              onChange={setQuery}
              totalCount={data?.totalCount}
              itemsCount={data?.systems?.length || 0}
            />
            <Text size="sm" c="dimmed">
              Showing {data?.systems?.length || 0} of {data?.totalCount || 0} systems
            </Text>
          </Group>
        </PageHeader>

        <Box mt="lg">
          <DataTable
            data={data?.systems || []}
            columns={columns}
            loading={isLoading}
            onRowClick={handleRowClick}
            pagination={{
              page: data?.page || 1,
              pageSize: query.pageSize,
              total: data?.totalCount || 0,
              onPageChange: handlePageChange
            }}
            placeholder={
              <Text c="dimmed" ta="center">
                No systems found matching your criteria
              </Text>
            }
            maxHeight="69vh"
          />
        </Box>
      </Box>
    </ProtectedComponent>
  )
}